import { PlusOutlined } from "@ant-design/icons";
import { Button, Empty, Table, Typography } from "antd";
import type { ColumnsType } from "antd/lib/table";
import { ReactNode } from "react";
import { useTranslation } from "react-i18next";

import type { Operator } from "@/providers/smarta-accounts/accounts-api-client";
import { ContentWrapper } from "~/components/common/styledComponents";
import { AssigneeSelect } from "./AssigneeSelect";
import { Description, PageHeader, Title } from "./styles";
import { Lead } from "./types";

const NO_VALUE = "-";

export interface OnboardingViewProps {
  leads: Lead[];
  total: number;
  page: number;
  pageSize: number;
  loading: boolean;
  error: boolean;
  onPageChange: (page: number) => void;
  onRetry: () => void;
  onNewLead: () => void;
  operators: Operator[];
  /** Leads whose assignee change is still in flight. */
  savingLeadIds: ReadonlySet<string>;
  onAssigneeChange: (lead: Lead, operatorId: string | undefined) => void;
  /**
   * Each lead's number of files, by public id. A lead that is missing is still
   * loading; `null` means the count could not be loaded. Both show a dash.
   */
  fileCounts: ReadonlyMap<string, number | null>;
  /**
   * The lead's documents (SMT-1380). When given, every row expands into what
   * it returns; without it the table has no expand control.
   */
  renderExpandedLead?: (lead: Lead) => ReactNode;
}

export const OnboardingView = ({
  leads,
  total,
  page,
  pageSize,
  loading,
  error,
  onPageChange,
  onRetry,
  onNewLead,
  operators,
  savingLeadIds,
  onAssigneeChange,
  fileCounts,
  renderExpandedLead,
}: OnboardingViewProps) => {
  const { t } = useTranslation("Onboarding");

  const columns: ColumnsType<Lead> = [
    {
      // Name with the email under it, as in the Phase 1 mockup. Two-line cell
      // in the BO's convention (AccountRow): 12px secondary text below.
      title: t("leads.lead"),
      key: "lead",
      render: (_, lead) => (
        <>
          <div>{lead.displayName}</div>
          <Typography.Text style={{ fontSize: 12 }} type="secondary">
            {lead.email}
          </Typography.Text>
        </>
      ),
    },
    { title: t("leads.legalForm"), dataIndex: "legalForm" },
    {
      title: t("leads.files"),
      key: "files",
      render: (_, lead) => fileCounts.get(lead.publicId) ?? NO_VALUE,
    },
    {
      title: t("leads.assignee"),
      key: "assignee",
      render: (_, lead) => (
        <AssigneeSelect
          lead={lead}
          operators={operators}
          saving={savingLeadIds.has(lead.publicId)}
          onChange={onAssigneeChange}
        />
      ),
    },
  ];

  return (
    <ContentWrapper>
      <PageHeader>
        <div>
          <Title>{t("page.title")}</Title>
          <Description>{t("page.description")}</Description>
        </div>
        <Button type="primary" icon={<PlusOutlined />} onClick={onNewLead}>
          {t("newLead.open")}
        </Button>
      </PageHeader>
      {error ? (
        <Empty description={t("leads.error")}>
          <Button onClick={onRetry}>{t("leads.retry")}</Button>
        </Empty>
      ) : (
        <Table<Lead>
          rowKey="publicId"
          columns={columns}
          dataSource={leads}
          loading={loading}
          locale={{ emptyText: t("leads.empty") }}
          expandable={
            renderExpandedLead
              ? { expandedRowRender: renderExpandedLead }
              : undefined
          }
          pagination={{
            current: page,
            pageSize,
            total,
            showSizeChanger: false,
            onChange: onPageChange,
          }}
        />
      )}
    </ContentWrapper>
  );
};
